<template>
    <template v-if="isVisible">
        <!-- Mobile: compact inline banner -->
        <div id="preferred_language_notice" class="md:hidden bg-white border border-[#DFDFDF] rounded-xl p-3 relative" data-testid="preferred-language-notice">
            <div class="flex items-center gap-3 pr-6">
                <div class="flex flex-col gap-1 min-w-0">
                    <p class="body-small text-valence-grey-750">
                        Chat language set to
                        <StatusBadge text="Saved!" :is-showing="isSavedShowing" />
                        <select
                            v-model="selectedLanguageCode"
                            aria-label="language selection"
                            :disabled="isSaving"
                            class="hover:opacity-85 bg-white w-auto disabled:opacity-50 focus-visible:outline-hidden font-semibold">
                            <option v-for="choice in choices" :key="choice.code" :value="choice.code" :selected="choice.code === selectedLanguageCode">{{ choice.display }}</option>
                        </select>
                    </p>
                    <p class="details text-valence-grey-600">Change anytime in profile settings.</p>
                </div>
            </div>
            <button
                aria-label="Close language notice"
                class="hover:bg-slate-100 py-1 px-2 top-2 right-1 absolute rounded-full"
                data-testid="close-preferred-language-notice"
                @click="isVisible = false">
                <i class="bi bi-x-lg icon-bold text-valence-grey-750 text-xs" aria-hidden="true" />
            </button>
        </div>

        <!-- Desktop: compact fixed banner at bottom-right -->
        <div class="hidden md:block fixed bottom-0 right-0 mb-6 mx-6 bg-white border border-[#DFDFDF] shadow-sm max-w-xs z-30 rounded-xl p-3">
            <div class="flex items-center gap-3 pr-6">
                <div class="flex flex-col gap-1 min-w-0">
                    <p class="body-small text-valence-grey-750">
                        Chat language set to
                        <StatusBadge text="Saved!" :is-showing="isSavedShowing" />
                        <select
                            v-model="selectedLanguageCode"
                            aria-label="language selection"
                            :disabled="isSaving"
                            class="hover:opacity-85 bg-white w-auto disabled:opacity-50 focus-visible:outline-hidden font-semibold">
                            <option v-for="choice in choices" :key="choice.code" :value="choice.code" :selected="choice.code === selectedLanguageCode">{{ choice.display }}</option>
                        </select>
                    </p>
                    <p class="details text-valence-grey-600">Change anytime in profile settings.</p>
                </div>
            </div>
            <button
                aria-label="Close language notice"
                class="hover:bg-slate-100 py-1 px-2 top-2 right-1 absolute rounded-full"
                data-testid="close-preferred-language-notice"
                @click="isVisible = false">
                <i class="bi bi-x-lg icon-bold text-valence-grey-750 text-xs" aria-hidden="true" />
            </button>
        </div>
    </template>
</template>

<script setup>
import { getCookie } from "/js/utils.js";
import { useTimedShow } from "~vue/composables/useTimedShow";
import { inject, onMounted, ref, watch } from "vue";

import StatusBadge from "./StatusBadge.vue";

const userDetails = inject("user_details");

const isVisible = ref(false);
const isSaving = ref(false);
const { isShowing: isSavedShowing, show: showSaved } = useTimedShow({ initialState: false, duration: 2000 });
const choices = ref([]);
const selectedLanguageCode = ref("en");
const browserLanguageDisplay = ref("");

onMounted(async () => {
    const hasLanguage = userDetails.value.default_language && userDetails.value.default_language !== "no_default";
    if (hasLanguage) {
        return;
    }

    choices.value = await _fetchChoices();
    if (!choices.value) {
        return;
    }

    const codes = choices.value.map((choice) => choice.code);
    for (const longCode of navigator.languages) {
        const shortCode = longCode.slice(0, 2);
        if (codes.includes(shortCode)) {
            browserLanguageDisplay.value = _getLanguageDisplay(shortCode);
            selectedLanguageCode.value = shortCode;
            break;
        }
    }

    const ok = await _saveLanguagePreference(selectedLanguageCode.value);
    // If we couldn't save the initial browser setting, we shouldn't tell them we defaulted the
    // language at all.
    if (ok) {
        isVisible.value = true;
    }
});

watch(selectedLanguageCode, async (newCode) => {
    const ok = await _saveLanguagePreference(newCode);
    if (ok) {
        showSaved();
    }
});

function _getLanguageDisplay(code) {
    const choice = choices.value.find((c) => c.code == code);
    return choice?.display;
}

async function _fetchChoices() {
    const response = await fetch("/api/chat/language_choices");
    if (!response.ok) {
        console.error("Failed to get language choices for preferred language notice: " + response.status);
        return;
    }
    return await response.json();
}

async function _saveLanguagePreference(code) {
    if (isSaving.value) {
        return false;
    }

    const language = _getLanguageDisplay(code);
    if (!language) {
        return false;
    }

    isSaving.value = true;
    try {
        const response = await fetch("/accounts/profile/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": getCookie("csrftoken"),
            },
            body: JSON.stringify({
                detail: "default_language",
                default_language: language,
            }),
        });
        if (!response.ok) {
            console.error("Failed to save language preference: " + response.status);
        }
        return response.ok;
    } catch (err) {
        console.error("Failed to save language preference: " + err);
        return false;
    } finally {
        isSaving.value = false;
    }
}
</script>
